import React, { useMemo, useState } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import {
  arrivalsByTimeslot,
  formatSlotClock,
  groupSizeHistogram,
  overdueReservations,
} from '../../../lib/liveBriefing';
import type { GuestReviewOutletRow, PerformanceMetric } from '../../../types';
import { WeatherTile } from '../../ExternalSignals';
import { NorthStarHero } from '../../NorthStarHero';
import {
  MetricInsightTile,
  OutletHeroSignalCard,
  type HeroSignal,
} from '../../PerformanceHeroCards';
import { buildOutletHeroSignals } from '../../PerformanceView';
import { RecoverySection } from '../../RecoverySection';
import { GuestFlowBar } from '../../ribbon/GuestFlowBar';
import { pointGuests, RibBenchmark, RibStat, TimelineBand } from '../../ribbon/ribbonPieces';
import { ScoreCard } from '../../ScoreCard';
import { SellItemsSection } from '../../SellItemsSection';
import { ServiceTimelineGantt } from '../../ServiceTimelineGantt';
import { AnimatedNumber } from '../../shared/AnimatedNumber';
import { Ring } from '../../shared/Icon';
import { SituationBrief } from '../../SituationBrief';
import {
  buildPerformanceOutletTableColumns,
  canShowOutletTrendHighlights,
  OUTLET_TREND_MIN_COVERS,
  OutletMetricCell,
  OutletReviewCell,
  OutletTableHeader,
  OutletTableSectionRow,
  OutletTableToggle,
  outletTrendScore,
  pickOutletTrendHighlights,
  sortOutletRows,
  type OutletTableSort,
} from '../../outletPerformanceTable';
import { liveWidgetScope } from './liveScope';
import { useAssistantWidgetData } from './WidgetDataContext';
import {
  HorizonHeroWidget,
  HorizonSignalsWidget,
  HorizonWeekWidget,
} from './HorizonWidgets';
import {
  resolveHighlightsVariant,
  resolveRibKpiColumn,
  resolveScorecardMetricKey,
  type WidgetSpec,
} from './widgetTypes';

function Empty({ text }: { text: string }) {
  return <p className="maitre-tile-empty">{text}</p>;
}

function pickHighlightsCard(
  data: NonNullable<ReturnType<typeof useAssistantWidgetData>>['performance'],
  heroNuggets: NonNullable<ReturnType<typeof useAssistantWidgetData>>['heroNuggets'],
  params: Record<string, string> | undefined,
): { kind: 'outlet' | 'pattern'; signal: HeroSignal } | null {
  if (!data) return null;
  const variant = resolveHighlightsVariant(params?.variant);
  const cancelMetric = data.metrics.find(m => m.key === 'cancel');
  const noshowMetric = data.metrics.find(m => m.key === 'noshow');
  const fleetCancelRate = typeof cancelMetric?.value === 'number' ? cancelMetric.value : 0;
  const fleetNoshowRate = typeof noshowMetric?.value === 'number' ? noshowMetric.value : 0;
  const { outletLowlights, outletHighlights } = buildOutletHeroSignals(
    data.outletRows,
    fleetCancelRate,
    fleetNoshowRate,
    fleetCancelRate + fleetNoshowRate,
    data.aggregationLabel,
    data.comparePeriod?.label || 'the prior period',
    '',
    data.comparable ?? false,
    data.comparePeriod?.label ?? null,
  );
  if (variant === 'attention') {
    const signal =
      (params?.outletId
        ? outletLowlights.find(s => s.outletId === params.outletId)
        : outletLowlights[0]) ?? null;
    return signal ? { kind: 'outlet', signal } : null;
  }
  if (variant === 'leading') {
    const signal =
      (params?.outletId
        ? outletHighlights.find(s => s.outletId === params.outletId)
        : outletHighlights[0]) ?? null;
    return signal ? { kind: 'outlet', signal } : null;
  }
  const patterns: HeroSignal[] = (heroNuggets ?? []).map(n => ({
    tone: n.tone === 'risk' ? 'risk' : 'win',
    icon: n.icon as HeroSignal['icon'],
    label: n.label,
    value: n.value,
    detail: n.detail,
    action: n.action,
    priority: n.priority,
    patternKey: n.label,
  }));
  const signal =
    (params?.patternKey
      ? patterns.find(s => s.patternKey === params.patternKey || s.label === params.patternKey)
      : patterns[0]) ?? null;
  if (signal) return { kind: 'pattern', signal };
  if (outletLowlights[0]) return { kind: 'outlet', signal: outletLowlights[0] };
  if (outletHighlights[0]) return { kind: 'outlet', signal: outletHighlights[0] };
  return null;
}

function OutletTableWidget() {
  const ctx = useAssistantWidgetData();
  const data = ctx?.performance;
  const [sort, setSort] = useState<OutletTableSort>({ key: 'covers', dir: 'desc' });
  const [showFull, setShowFull] = useState(false);
  const posEnabled = !!ctx?.connected.includes('pos');
  const reviewsEnabled =
    !!ctx?.connected.includes('reviews') &&
    (ctx.guestReviewsLoading || !!ctx.guestReviews?.enabled);
  const reviewByOutletId = useMemo(() => {
    const map = new Map<string, GuestReviewOutletRow>();
    for (const r of ctx?.guestReviews?.outletRows ?? []) {
      map.set(r.restaurantId, r);
    }
    return map;
  }, [ctx?.guestReviews?.outletRows]);
  const tableColumns = useMemo(
    () => (data ? buildPerformanceOutletTableColumns(data, posEnabled, reviewsEnabled) : []),
    [data, posEnabled, reviewsEnabled],
  );
  const trendMetricKeys = useMemo(
    () => tableColumns.filter(c => c.kind !== 'outlet').map(c => c.key),
    [tableColumns],
  );
  const sortedRows = useMemo(() => {
    if (!data) return [];
    return sortOutletRows(data.outletRows, sort, !data.comparable, reviewByOutletId);
  }, [data, sort, reviewByOutletId]);
  const highlights = useMemo(() => {
    if (!data) return { top: [], bottom: [], eligibleCount: 0 };
    return pickOutletTrendHighlights(
      data.outletRows.filter(r => r.periodCovers >= OUTLET_TREND_MIN_COVERS),
      row =>
        outletTrendScore(
          row,
          data.comparable ?? false,
          trendMetricKeys,
          reviewByOutletId.get(row.outlet.id),
        )?.score ?? null,
      row => row.outlet.id,
    );
  }, [data, trendMetricKeys, reviewByOutletId]);
  if (!data) return <Empty text={ctx?.performanceLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noPerf')} />;
  const comparable = data.comparable ?? false;
  const showHighlights =
    canShowOutletTrendHighlights(highlights.eligibleCount, comparable) && !showFull;
  const rows = showHighlights
    ? { top: highlights.top, bottom: highlights.bottom }
    : { all: sortedRows };
  const renderRow = (row: (typeof data.outletRows)[number]) => {
    const metricByKey = Object.fromEntries((row.metrics ?? []).map(m => [m.key, m]));
    const review = reviewByOutletId.get(row.outlet.id);
    return (
      <tr key={row.outlet.id} className="opt-metric-row sm-row">
        {tableColumns.map(col => {
          if (col.kind === 'outlet') {
            return (
              <td key={col.key} className="opt-metric-out sm-out">
                <div className="sm-out-main">
                  <b>{row.outlet.name}</b>
                  <span>
                    {row.outlet.region} · {row.outlet.city}
                  </span>
                </div>
              </td>
            );
          }
          if (col.kind === 'reviewRating' || col.kind === 'reviewCount') {
            return (
              <OutletReviewCell
                key={col.key}
                review={review}
                kind={col.kind}
                comparable={comparable}
              />
            );
          }
          return (
            <OutletMetricCell
              key={col.key}
              metric={metricByKey[col.key]}
              comparable={comparable}
            />
          );
        })}
      </tr>
    );
  };
  return (
    <div className="ol-card optbl-card">
      <div className="optbl-scroll sm-scroll">
        <table className="opt-metric-table sm-table">
          <OutletTableHeader
            columns={tableColumns}
            sort={sort}
            onSort={setSort}
            sortDisabled={showHighlights}
          />
          <tbody>
            {showHighlights ? (
              <>
                <OutletTableSectionRow label={i18n.t('assistant:widget.topMomentum')} colSpan={tableColumns.length} tone="top" />
                {rows.top?.map(renderRow)}
                <OutletTableSectionRow label={i18n.t('assistant:widget.bottomMomentum')} colSpan={tableColumns.length} tone="bottom" />
                {rows.bottom?.map(renderRow)}
              </>
            ) : (
              sortedRows.map(renderRow)
            )}
          </tbody>
        </table>
      </div>
      {canShowOutletTrendHighlights(highlights.eligibleCount, comparable) && (
        <OutletTableToggle
          expanded={showFull}
          totalCount={data.outletRows.length}
          onToggle={() => setShowFull(v => !v)}
        />
      )}
    </div>
  );
}

function RibKpisWidget({ column }: { column: string }) {
  const ctx = useAssistantWidgetData();
  if (!ctx?.live) return <Empty text={ctx?.liveLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noLive')} />;
  const scope = liveWidgetScope(ctx.live);
  const { scopedKpis, bench, fc, bookedByClose, lostGapCovers, hasPos } = scope;
  const clock = fc ? fc.nowH * 60 : 0;
  if (column === 'rightNow') {
    return (
      <div className="rib-col now">
        <div className="rib-col-head">
          <span className="rib-col-lab">
            <span className="rib-pulse" />
            Right now
          </span>
          <span className="rib-col-sub">{fmt.clock(clock)} · live floor</span>
        </div>
        <div className="rib-now-hero">
          <div className="rib-now-ring">
            <Ring pct={scopedKpis.occupancy} size={74} stroke={7} color="var(--accent)">
              {`${scopedKpis.occupancy}%`}
            </Ring>
            <RibBenchmark
              baseline={bench?.occupancy}
              current={scopedKpis.occupancy}
              formatValue={v => `${v}%`}
            />
          </div>
          <div className="rib-now-side">
            <div className="rib-now-row">
              <div className="rib-now-main">
                <b>
                  <AnimatedNumber value={scopedKpis.seated} />
                </b>
                <span>of {fmt.num(scopedKpis.seats)} seats</span>
              </div>
            </div>
            <div className="rib-now-row">
              <div className="rib-now-main">
                <b>{scopedKpis.occTables}</b>
                <span>of {scopedKpis.tables} tables</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (column === 'stillToCome') {
    const peakGuests = fc ? pointGuests(fc.peak) : 0;
    return (
      <div className="rib-col future">
        <div className="rib-col-head">
          <span className="rib-col-lab">Still to come</span>
        </div>
        <RibStat
          tense="booked"
          value={<span style={{ color: 'var(--accent)' }}>+{scopedKpis.incoming}</span>}
          label="booked · next 2h"
          benchmark={
            <RibBenchmark
              baseline={bench?.incoming}
              current={scopedKpis.incoming}
              formatValue={v => `+${v}`}
            />
          }
        />
        <RibStat
          tense="booked"
          value={fmt.num(bookedByClose)}
          label="booked by close"
        />
        {fc ? (
          <RibStat
            tense="booked"
            value={
              <>
                {fmt.num(peakGuests)}
                <span className="rib-u"> @ </span>
                {fmt.clock(fc.peakMin)}
              </>
            }
            label="peak guests · booked"
          />
        ) : null}
      </div>
    );
  }
  return (
    <div className="rib-col past">
      <div className="rib-col-head">
        <span className="rib-col-lab">So far today</span>
      </div>
      <RibStat tense="fact" value={fmt.num(scopedKpis.covers)} label="guests served" />
      <RibStat
        tense="fact"
        value={fmt.num(lostGapCovers)}
        label="unrebooked cancels & no-shows"
      />
      {!hasPos && (
        <RibStat
          tense="fact"
          value={
            <>
              {scopedKpis.seatTime}
              <span className="rib-u">m</span>
            </>
          }
          label="avg dwell so far"
        />
      )}
    </div>
  );
}

function BriefGroups() {
  const ctx = useAssistantWidgetData();
  const reservations = ctx?.live?.serviceTimeline?.reservations ?? [];
  const groups = groupSizeHistogram(reservations);
  const maxGroupGuests = groups[0]?.guests ?? 1;
  return (
    <div className="live-brief-panel">
      <div className="live-brief-lab">Group sizes</div>
      {groups.length === 0 ? (
        <p className="live-brief-empty">No active reservations</p>
      ) : (
        <ul className="live-brief-bars">
          {groups.map(g => (
            <li key={g.people}>
              <div className="live-brief-bar-label">
                <strong>
                  {g.bookings}×{g.people}
                </strong>
                <span>
                  {g.guests} guest{g.guests === 1 ? '' : 's'}
                </span>
              </div>
              <div className="live-brief-bar-track" aria-hidden>
                <div
                  className="live-brief-bar-fill size"
                  style={{ width: `${Math.max(8, (g.guests / maxGroupGuests) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BriefArrivals() {
  const ctx = useAssistantWidgetData();
  const reservations = ctx?.live?.serviceTimeline?.reservations ?? [];
  const arrivals = arrivalsByTimeslot(reservations);
  const maxArrivalGuests = Math.max(1, ...arrivals.map(a => a.guests));
  const peakArrival = arrivals.reduce<(typeof arrivals)[number] | null>(
    (best, slot) => (!best || slot.guests > best.guests ? slot : best),
    null,
  );
  return (
    <div className="live-brief-panel arrivals-panel">
      <div className="live-brief-lab">Arrivals by slot</div>
      {arrivals.length === 0 ? (
        <p className="live-brief-empty">No arrivals booked</p>
      ) : (
        <ul className="live-brief-bars arrivals">
          {arrivals.map(slot => (
            <li key={slot.timeMs} className={peakArrival?.timeMs === slot.timeMs ? 'peak' : undefined}>
              <span className="live-brief-time">{formatSlotClock(slot.timeMs)}</span>
              <div className="live-brief-bar-track" aria-hidden>
                <div
                  className="live-brief-bar-fill arrival"
                  style={{
                    width: `${Math.max(6, (slot.guests / maxArrivalGuests) * 100)}%`,
                  }}
                />
              </div>
              <span className="live-brief-bar-val">
                <strong>{slot.guests}</strong>
                <span className="live-brief-muted">
                  {' '}
                  guest{slot.guests === 1 ? '' : 's'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BriefAttention() {
  const ctx = useAssistantWidgetData();
  const reservations = ctx?.live?.serviceTimeline?.reservations ?? [];
  const overdue = overdueReservations(reservations);
  const kitchen = ctx?.live?.kitchenBriefing?.outlets?.[0];
  const vipCount = kitchen?.vipParties.length ?? 0;
  const requestCount = kitchen?.requests.length ?? 0;
  if (!overdue.length && !vipCount && !requestCount) {
    return <Empty text={i18n.t('assistant:widget.allClear')} />;
  }
  return (
    <div className="live-brief-attention">
      <div className="live-brief-attention-head">
        <div className="live-brief-lab">Needs attention</div>
      </div>
      {kitchen && kitchen.vipParties.length > 0 && (
        <div className="live-brief-vip-row">
          {kitchen.vipParties.map((v, i) => (
            <div
              key={`${v.reservationId ?? v.startMs}-${v.peopleCount}-${i}`}
              className="live-brief-vip-chip"
            >
              <span className="live-brief-vip-mark">VIP</span>
              <span className="live-brief-time">{formatSlotClock(v.startMs)}</span>
            </div>
          ))}
        </div>
      )}
      {overdue.length > 0 && (
        <p className="live-brief-empty">{overdue.length} past end</p>
      )}
      {requestCount > 0 && (
        <p className="live-brief-empty">
          {requestCount} reservation note{requestCount === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

export function WidgetRenderer({ spec }: { spec: WidgetSpec }) {
  const ctx = useAssistantWidgetData();
  const params = spec.params ?? {};

  if (spec.type === 'perf.scorecard') {
    const metric = ctx?.performance?.metrics.find(
      (m): m is PerformanceMetric =>
        m.key === resolveScorecardMetricKey(params.metricKey),
    );
    if (!metric) {
      return <Empty text={ctx?.performanceLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.metricUnavailable')} />;
    }
    return (
      <ScoreCard
        m={metric}
        comparable={ctx?.performance?.comparable ?? false}
      />
    );
  }

  if (spec.type === 'perf.northStar') {
    if (!ctx?.northStar) return <Empty text={i18n.t('assistant:widget.nsUnset')} />;
    return <NorthStarHero payload={ctx.northStar} />;
  }

  if (spec.type === 'perf.highlightsCard') {
    const picked = pickHighlightsCard(ctx?.performance, ctx?.heroNuggets, spec.params);
    if (!picked) {
      const loading = !!ctx?.performanceLoading || !!ctx?.heroNuggetsLoading;
      return <Empty text={loading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noHighlight')} />;
    }
    if (picked.kind === 'outlet') {
      return (
        <OutletHeroSignalCard
          signal={picked.signal}
          outletRows={ctx?.performance?.outletRows ?? []}
        />
      );
    }
    return <MetricInsightTile tile={picked.signal} />;
  }

  if (spec.type === 'perf.situation') {
    const data = ctx?.performance;
    return (
      <SituationBrief
        embedded
        pack={ctx?.situationPack}
        loading={ctx?.situationLoading}
        onAct={() => undefined}
        restaurantId={ctx?.restaurantId}
        periodStart={data?.periodStart ?? data?.dateRange?.start ?? null}
        periodEnd={data?.periodEnd ?? data?.dateRange?.end ?? null}
        compareStart={data?.comparePeriod?.start ?? null}
        compareEnd={data?.comparePeriod?.end ?? null}
      />
    );
  }

  if (spec.type === 'perf.outletTable') {
    return <OutletTableWidget />;
  }

  if (spec.type === 'live.curve') {
    if (!ctx?.live) return <Empty text={ctx?.liveLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noLive')} />;
    const scope = liveWidgetScope(ctx.live);
    if (!scope.fc) return <Empty text={i18n.t('assistant:widget.noGuestTimeline')} />;
    return (
      <div className="rib-band-row">
        <TimelineBand
          fc={scope.fc}
          nowGuests={scope.scopedKpis.seated}
          benchGuests={scope.bench?.seated ?? null}
          shifts={scope.timelineShifts}
        />
      </div>
    );
  }

  if (spec.type === 'live.gantt') {
    if (!ctx?.live) return <Empty text={ctx?.liveLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noLive')} />;
    const scope = liveWidgetScope(ctx.live);
    if (!scope.fc) return <Empty text={i18n.t('assistant:widget.noBookingsTimeline')} />;
    return (
      <ServiceTimelineGantt
        forecast={scope.fc}
        timeline={scope.scopedTimeline}
        nowGuests={scope.scopedKpis.seated}
        showOutlet={false}
      />
    );
  }

  if (spec.type === 'live.ribKpis') {
    return <RibKpisWidget column={resolveRibKpiColumn(params.column)} />;
  }

  if (spec.type === 'live.segbar') {
    if (!ctx?.live) return <Empty text={ctx?.liveLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noLive')} />;
    const scope = liveWidgetScope(ctx.live);
    return (
      <GuestFlowBar
        served={scope.scopedKpis.covers}
        incoming={scope.scopedKpis.incoming}
        stillBooked={scope.stillBooked}
        bookedByClose={scope.bookedByClose}
      />
    );
  }

  if (spec.type === 'live.occupancyRing') {
    if (!ctx?.live) return <Empty text={ctx?.liveLoading ? i18n.t('assistant:widget.loading') : i18n.t('assistant:widget.noLive')} />;
    const scope = liveWidgetScope(ctx.live);
    return (
      <div className="rib-now-ring">
        <Ring pct={scope.scopedKpis.occupancy} size={74} stroke={7} color="var(--accent)">
          {`${scope.scopedKpis.occupancy}%`}
        </Ring>
      </div>
    );
  }

  if (spec.type === 'live.briefGroups') return <BriefGroups />;
  if (spec.type === 'live.briefArrivals') return <BriefArrivals />;
  if (spec.type === 'live.briefAttention') return <BriefAttention />;

  if (spec.type === 'live.recovery') {
    return <RecoverySection recovery={ctx?.live?.recovery ?? null} />;
  }

  if (spec.type === 'live.sell') {
    return (
      <SellItemsSection
        outlets={ctx?.live?.outlets ?? []}
        connected={ctx?.connected ?? []}
      />
    );
  }

  if (spec.type === 'live.weather') {
    return (
      <WeatherTile
        outlets={(ctx?.live?.outlets ?? []).filter(o => !o.closed)}
        connected={ctx?.connected ?? []}
      />
    );
  }

  if (spec.type === 'horizon.week') {
    return <HorizonWeekWidget outletId={params.outletId} />;
  }
  if (spec.type === 'horizon.hero') {
    return <HorizonHeroWidget outletId={params.outletId} />;
  }
  if (spec.type === 'horizon.signals') {
    return <HorizonSignalsWidget outletId={params.outletId} />;
  }

  return <Empty text={i18n.t('assistant:widget.unknown')} />;
}
